import React from 'react';
import { Dialog, DialogContent, useMediaQuery, useTheme } from '@mui/material';
import Tooltip from './Tooltip';

/**
 * Компонент модального окна для шагов туториала
 *
 * @param {Object} props - свойства компонента
 * @param {boolean} props.open - открыто ли модальное окно
 * @param {Object} props.position - позиция модального окна {top, left}
 * @param {string} props.title - заголовок шага
 * @param {string} props.content - содержимое шага
 * @param {string} props.placement - расположение подсказки
 * @param {function} props.onNext - обработчик нажатия кнопки "Далее"
 * @param {function} props.onPrev - обработчик нажатия кнопки "Назад"
 * @param {function} props.onSkip - обработчик нажатия кнопки "Пропустить"
 * @param {boolean} props.showPrev - показывать ли кнопку "Назад"
 * @param {boolean} props.isLastStep - последний ли это шаг
 * @param {number} props.currentStep - текущий шаг
 * @param {number} props.totalSteps - общее количество шагов
 */
const TutorialDialog = ({
  open,
  position,
  title,
  content,
  placement,
  onNext,
  onPrev,
  onSkip,
  showPrev,
  isLastStep,
  currentStep,
  totalSteps,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Определяем позицию для разных экранов
  let dialogPosition = {};

  if (!isMobile) {
    // На десктопе всегда располагаем по центру экрана
    dialogPosition = {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      fullScreen={false}
      hideBackdrop={false}
      PaperProps={{
        elevation: 0,
        sx: {
          position: 'fixed',
          zIndex: 20000,
          ...dialogPosition,
          borderRadius: '16px',
          p: 3,
          m: 0,
          width: { xs: '95vw', sm: 400 },
          maxWidth: { xs: '95vw', sm: 400 },
          maxHeight: { xs: '90vh', sm: '80vh' },
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 6,
          // Анимация появления
          animation: 'fadeIn 0.3s ease-out',
          '@keyframes fadeIn': {
            from: {
              opacity: 0,
              transform: isMobile
                ? 'translateY(20px)'
                : 'translate(-50%, -60%)',
            },
            to: {
              opacity: 1,
              transform: isMobile ? 'translateY(0)' : 'translate(-50%, -50%)',
            },
          },
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(2px)',
        },
      }}
      TransitionProps={{
        timeout: 300,
      }}
    >
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Tooltip
          title={title}
          content={content}
          position={position}
          placement={placement}
          onNext={onNext}
          onPrev={onPrev}
          onSkip={onSkip}
          showPrev={showPrev}
          showNext={
            typeof open.showNextButton === 'boolean'
              ? open.showNextButton
              : true
          }
          isLastStep={isLastStep}
          currentStep={currentStep}
          totalSteps={totalSteps}
        />
      </DialogContent>
    </Dialog>
  );
};

export default TutorialDialog;
